import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail } from "lucide-react";
import { SaveBadge } from "@/components/SaveBadge";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Collects an email + terms consent for a guest (no-account) purchase, then calls onSubmit.
export const GuestCheckoutDialog = ({ open, onOpenChange, title, priceLabel, onSubmit, upsellHref }) => {
  const [email, setEmail] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const valid = EMAIL_RE.test(email) && terms;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try { await onSubmit(email.trim().toLowerCase()); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="guest-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
          <DialogDescription>
            No account needed — just enter your email. We'll set up access instantly after payment and you can add a password later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Email address</Label>
            <div className="relative mt-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="guest-email-input"
                placeholder="you@example.com" className="rounded-sm pl-9" onKeyDown={(e) => e.key === "Enter" && submit()} />
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="guest-terms-checkbox" className="mt-0.5" />
            <span>I agree to the <Link to="/legal/terms" className="text-accent underline" target="_blank">Terms</Link>, <Link to="/legal/privacy" className="text-accent underline" target="_blank">Privacy Policy</Link> and that this is not legal advice.</span>
          </label>
        </div>
        <Button onClick={submit} disabled={busy || !valid} data-testid="guest-continue-btn"
          className="w-full rounded-sm h-11 mt-2 gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} Continue to payment{priceLabel ? ` — ${priceLabel}` : ""}
        </Button>
        {upsellHref && (
          <Link to={upsellHref} data-testid="guest-upsell-link"
            className="block text-center text-sm text-accent font-semibold hover:underline mt-1">
            Need a few? Add 2 more forms for $9.95 → <SaveBadge testid="guest-upsell-save-badge" className="ml-1 align-middle" />
          </Link>
        )}
      </DialogContent>
    </Dialog>
  );
};
